// packages/chatbot/src/components/Sidebar.tsx

import React from 'react';
import { PlusCircle, MessageSquare, ChevronRight, X } from 'lucide-react';
import type { ConversationSummary, ChatbotLayoutMode } from '../types';
import { Button, Spinner, Tooltip, IconButton } from '@dopomogai/ui';
import clsx from 'clsx';
import { getContrastingTextColor } from '../utils/colorUtils';
import { NEW_CHAT_ID } from '../utils/constants';

const formatDisplayDate = (isoDate: string | null): string => {
  if (!isoDate) return 'N/A';
  const date = new Date(isoDate);
  const now = new Date();
  const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;

  const diffDays = Math.floor(diffSeconds / 86400);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};

interface SidebarProps {
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  onSelectConversation: (id: string | null) => void;
  isLoading: boolean;
  isMoreConversationsLoading: boolean;
  hasMoreConversations: boolean;
  onLoadMoreConversations: () => void;
  primaryColor?: string;
  showLayoutSwitcher?: boolean;
  onLayoutChange?: (newLayout: ChatbotLayoutMode) => void;
  hideBranding?: boolean;
  // NEW: close control inside the sidebar
  onCloseSidebar?: () => void;
}

export function Sidebar({
  conversations,
  activeConversationId,
  onSelectConversation,
  isLoading,
  isMoreConversationsLoading,
  hasMoreConversations,
  onLoadMoreConversations,
  primaryColor,
  showLayoutSwitcher,
  onLayoutChange,
  hideBranding,
  onCloseSidebar,
}: SidebarProps) {
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
    if (isAtBottom && !isMoreConversationsLoading && hasMoreConversations) {
      onLoadMoreConversations();
    }
  };

  return (
    <aside className="w-full h-full flex flex-col bg-slate-50">
      <div className="p-4 border-b-2 border-black flex-shrink-0 relative">
        <h2 className="text-xl font-bold text-slate-900">Chats</h2>
        {onCloseSidebar && (
          <div className="absolute right-2 top-2">
            <IconButton
              icon={<X />}
              ariaLabel="Close sidebar"
              variant="ghost"
              onClick={onCloseSidebar}
            />
          </div>
        )}
        <div className="mt-3">
          <Button
            text="New Chat"
            icon={<PlusCircle className="w-4 h-4" />}
            onClick={() => onSelectConversation(null)}
            fullWidth
            variant="primary"
            style={{
              backgroundColor: primaryColor,
              color: getContrastingTextColor(primaryColor),
            }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1" onScroll={handleScroll}>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <Spinner size="md" />
            <p className="mt-2 text-sm">Loading conversations...</p>
          </div>
        ) : conversations.length > 0 ? (
          <>
            {conversations.map((conv) => {
              const isActive = conv.id === activeConversationId;
              const previewText =
                conv.first_user_message_preview ||
                `Chat from ${new Date(conv.created_at).toLocaleDateString()}`;
              const lastUpdatedText = formatDisplayDate(conv.updated_at);

              return (
                <button
                  key={conv.id}
                  onClick={() =>
                    onSelectConversation(conv.id === NEW_CHAT_ID ? null : conv.id)
                  }
                  className={clsx(
                    'w-full text-left border-2 rounded-xl p-3',
                    'transition-all duration-150 ease-out transform',
                    // Remove green focus ring: use no ring on click/focus
                    'focus:outline-none focus-visible:outline-none',
                    isActive
                      ? 'bg-emerald-100 border-emerald-400 shadow-[2px_2px_0_0_#10b981]'
                      : 'bg-white border-black hover:-translate-y-px hover:-translate-x-px hover:shadow-[3px_3px_0_0_#000]'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 text-slate-500">
                      <MessageSquare size={20} />
                    </div>
                    <div className="flex-grow min-w-0">
                      <Tooltip text={previewText} position="top">
                        <p className="font-semibold text-sm text-slate-800 truncate">
                          {previewText}
                        </p>
                      </Tooltip>
                      <p className="text-xs text-slate-500">Updated: {lastUpdatedText}</p>
                    </div>
                    <div className="flex-shrink-0 text-slate-400">
                      <ChevronRight size={20} />
                    </div>
                  </div>
                </button>
              );
            })}
            {isMoreConversationsLoading && (
              <div className="flex justify-center items-center p-4">
                <Spinner size="sm" />
              </div>
            )}
          </>
        ) : (
          <div className="p-4 pt-8 text-center text-sm text-slate-500 flex flex-col items-center">
            <MessageSquare className="w-8 h-8 mb-2" />
            <p className="font-medium">No past conversations</p>
            <p className="text-xs">Start a new chat to begin.</p>
          </div>
        )}
      </div>

      {showLayoutSwitcher && (
        <div className="p-2 border-t-2 border-black">
          <IconButton
            icon={<ChevronRight />}
            onClick={() => onLayoutChange?.('full-right')}
            ariaLabel="Change chat layout"
            variant="ghost"
            className="w-full"
          />
        </div>
      )}

      {!hideBranding && (
        <div className="p-3 border-t-2 border-black text-xs text-center text-slate-500 flex-shrink-0">
          <a
            href="https://dpmg.xyz"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-emerald-500 hover:underline transition-colors"
          >
            Powered by DopomogAI
          </a>
        </div>
      )}
    </aside>
  );
}
